import { useCallback, useEffect, useRef } from 'react'
import { flushSync } from 'react-dom'
import './NavigationMotion.css'

const TABS = new Set(['home', 'swipe', 'map', 'messages', 'profile'])
const NAME = 'hubbing-menu'
const ATTRIBUTE = 'data-hubbing-menu-motion'
const OVERLAY = '[role="dialog"], [aria-modal="true"]'
const FIXED_OVERLAY = `${OVERLAY}, [style*="position: fixed"], [style*="position:fixed"]`

// The fallback never transforms the scrolling viewport: that would move fixed
// dialogs and Leaflet controls. View Transition snapshots do not move real DOM.
function visibleSurfaces(root) {
  const surfaces = []
  const visit = element => {
    if (element.hidden || ['STYLE', 'SCRIPT'].includes(element.tagName)) return
    const style = window.getComputedStyle(element)
    if (style.display === 'none') return
    if (style.display === 'contents') Array.from(element.children).forEach(visit)
    else if (style.position !== 'fixed' && !element.matches(FIXED_OVERLAY) && !element.querySelector(FIXED_OVERLAY)) surfaces.push(element)
  }
  if (root) Array.from(root.children).forEach(visit)
  return surfaces
}

export function useNavigationMotion({ tab, identity, contentRef, viewportRef, navigationRef, blocked = false }) {
  const state = useRef({ tab, identity, blocked })
  state.current = { tab, identity, blocked }
  const active = useRef(null)
  const cancel = useCallback((commit = true) => active.current?.stop(commit), [])

  useEffect(() => {
    const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    const interrupt = () => cancel()
    const visibility = () => { if (document.hidden) cancel() }
    document.addEventListener('pointerdown', interrupt, true)
    document.addEventListener('keydown', interrupt, true)
    document.addEventListener('click', interrupt, true)
    document.addEventListener('visibilitychange', visibility)
    window.addEventListener('resize', interrupt)
    preference?.addEventListener?.('change', interrupt)
    return () => {
      cancel(false)
      document.removeEventListener('pointerdown', interrupt, true)
      document.removeEventListener('keydown', interrupt, true)
      document.removeEventListener('click', interrupt, true)
      document.removeEventListener('visibilitychange', visibility)
      window.removeEventListener('resize', interrupt)
      preference?.removeEventListener?.('change', interrupt)
    }
  }, [cancel, identity])

  useEffect(() => { if (blocked) cancel() }, [blocked, cancel])

  return useCallback((nextTab, update) => {
    cancel()
    const current = state.current
    const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    const viewport = viewportRef?.current
    if (current.blocked || nextTab === current.tab || !TABS.has(nextTab) || !TABS.has(current.tab)
      || document.hidden || preference?.matches || document.querySelector(OVERLAY) || !viewport) {
      update()
      return
    }

    let applied = false
    let abandoned = false
    let restored = false
    let transition
    let frame
    let timeout
    const animations = []
    const oldName = viewport.style.getPropertyValue('view-transition-name')
    const oldPriority = viewport.style.getPropertyPriority('view-transition-name')
    const oldAttribute = document.documentElement.getAttribute(ATTRIBUTE)
    const apply = synchronous => {
      if (applied || abandoned) return
      applied = true
      if (synchronous) flushSync(update)
      else update()
    }
    const restore = () => {
      if (restored) return
      restored = true
      window.clearTimeout(timeout)
      if (frame !== undefined) window.cancelAnimationFrame(frame)
      animations.forEach(animation => animation.cancel())
      if (active.current !== record) return
      active.current = null
      if (oldName) viewport.style.setProperty('view-transition-name', oldName, oldPriority)
      else viewport.style.removeProperty('view-transition-name')
      if (oldAttribute === null) document.documentElement.removeAttribute(ATTRIBUTE)
      else document.documentElement.setAttribute(ATTRIBUTE, oldAttribute)
    }
    const record = {
      stop(commit = true) {
        if (commit) apply(false)
        else abandoned = true
        try { transition?.skipTransition?.() }
        catch { /* Browser cancellation cannot interrupt ordinary navigation. */ }
        finally { restore() }
      },
    }
    active.current = record

    const animate = (element, keyframes, duration) => {
      if (typeof element?.animate !== 'function') return
      try {
        const animation = element.animate(keyframes, { duration, easing: 'cubic-bezier(.2,.7,.3,1)' })
        animation.finished?.catch(() => {})
        animations.push(animation)
      } catch {
        // Effects are optional; changing menus must always work.
      }
    }
    const fallback = () => {
      apply(false)
      if (typeof window.requestAnimationFrame !== 'function') {
        restore()
        return
      }
      frame = window.requestAnimationFrame(() => {
        if (restored) return
        if (nextTab !== 'map' && !document.querySelector(OVERLAY)) visibleSurfaces(contentRef.current).forEach(element => animate(element, [
          { opacity: .8, transform: 'translateY(24px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ], 240))
        animate(navigationRef.current?.querySelector(`[data-navigation-icon="${nextTab}"]`), [
          { transform: 'scale(.96)' }, { transform: 'scale(1.07)', offset: .6 }, { transform: 'scale(1)' },
        ], 180)
        timeout = window.setTimeout(restore, 260)
      })
    }

    if (typeof document.startViewTransition !== 'function') {
      fallback()
      return
    }
    try {
      viewport.style.setProperty('view-transition-name', NAME)
      document.documentElement.setAttribute(ATTRIBUTE, '')
      transition = document.startViewTransition(() => apply(true))
      transition.ready?.catch(() => {})
      transition.updateCallbackDone?.catch(() => {})
      Promise.resolve(transition.finished).then(() => { apply(false); restore() }, () => { apply(false); restore() })
      // Guard against browser interruptions, without waiting for any request.
      timeout = window.setTimeout(() => record.stop(), 1000)
    } catch {
      restore()
      apply(false)
    }
  }, [cancel, contentRef, viewportRef, navigationRef])
}
